import type { ReactNode } from 'react';
import './utilityApps.css';

type UtilityAppVariant = 'standard' | 'wide' | 'fill' | 'fillWide';

interface UtilityAppLayoutProps {
  title: string;
  jobToBeDone: string;
  children: ReactNode;
  actions?: ReactNode;
  /** standard = normal flow; wide = calculator spacing; fill = remaining height; fillWide = full width + height */
  variant?: UtilityAppVariant;
  /**
   * Use the full app-shell main area without enabling fillWide height / overflow
   * behavior. Defaults true for consistent My Apps workspaces.
   */
  fullWidth?: boolean;
  /**
   * When false, omit the page title/subtitle chrome (nav already names the app).
   * Actions should then live in the page body. Defaults true.
   */
  showHeading?: boolean;
  className?: string;
}

/** Shared chrome for My Apps utility pages. */
export function UtilityAppLayout({
  title,
  jobToBeDone,
  children,
  actions,
  variant = 'standard',
  fullWidth = true,
  showHeading = true,
  className
}: UtilityAppLayoutProps) {
  const classes = [
    'utility-app',
    'ops-page',
    variant === 'wide' ? 'utility-app--wide' : '',
    variant === 'fill' ? 'utility-app--fill ops-fill-page' : '',
    variant === 'fillWide' ? 'utility-app--fill utility-app--fill-wide ops-fill-page' : '',
    fullWidth ? 'utility-app--full-width' : '',
    className ?? ''
  ]
    .filter(Boolean)
    .join(' ');

  const showHeader = showHeading || Boolean(actions);

  return (
    <div className={classes}>
      {showHeader ? (
        <header className="utility-app__header">
          {showHeading ? (
            <div className="utility-app__heading">
              <h1 className="utility-app__title">{title}</h1>
              <p className="utility-app__job">{jobToBeDone}</p>
            </div>
          ) : (
            <div className="utility-app__heading" aria-hidden="true" />
          )}
          {actions ? <div className="utility-app__actions">{actions}</div> : null}
        </header>
      ) : null}
      <div className="utility-app__body">{children}</div>
    </div>
  );
}
